import { apiRequest } from "../client";

export interface CustomerAddress {
  accountNo: string;
  sequenceNo: string;
  addressTypeID: string;
  addressNo: string;
  mooNo: string;
  villageName: string;
  soiName: string;
  roadName: string;
  province: string;
  district: string;
  subdistrict: string;
  postCode: string;
  locationID: string;
  houseStatusID: string;
  phone?: { type: string; number: string }[];
  canUpdate?: boolean;
}

/**
 * Query key for {@link getCustomerAddresses}. Shared so the update flow can
 * invalidate exactly what the list screen reads — a second literal spelled out
 * at the mutation site would silently stop matching the day this one changes.
 */
export const CUSTOMER_ADDRESSES_KEY = ["customer", "addresses"] as const;

export function getCustomerAddresses(): Promise<CustomerAddress[]> {
  return apiRequest("GET", "/v1/customer/address");
}

export function updateCustomerAddress(body: {
  pinCode: string;
  accountNo: string;
  oldAddress: CustomerAddress;
  newAddress: CustomerAddress;
  requestId?: string;
}): Promise<unknown> {
  return apiRequest("POST", "/v1/customer/address/update", body);
}

export function formatCustomerAddress(address: CustomerAddress): string {
  const moo = address.mooNo ?? "";
  const soi = address.soiName ?? "";
  const road = address.roadName ?? "";
  return [
    address.addressNo ?? "",
    moo.length > 0 ? `${moo}` : "",
    address.villageName ?? "",
    soi.length > 0 ? `${soi}` : "",
    road.length > 0 ? `${road}` : "",
    `${address.subdistrict ?? ""}`,
    `${address.district ?? ""}`,
    address.province ?? "",
    address.postCode ?? "",
  ]
    .filter((part) => part.length > 0)
    .join(" ");
}

const LOCK_PHONE_ACCOUNT_NO_PREFIX = "50";
const HIRE_PURCHASE_ACCOUNT_NO_PREFIX = "51";

export type ContractType = "lockPhone" | "hirePurchase";
/** Null for an account number outside both known prefixes. */
export function contractTypeOf(accountNo: string): ContractType | null {
  if (accountNo.startsWith(LOCK_PHONE_ACCOUNT_NO_PREFIX)) return "lockPhone";
  if (accountNo.startsWith(HIRE_PURCHASE_ACCOUNT_NO_PREFIX)) return "hirePurchase";
  return null;
}

/** Prefers a mobile number, then any other phone on file, else empty. */
export function pickCustomerPhone(address: CustomerAddress): string {
  const phones = address.phone ?? [];
  return (
    phones.find((entry) => entry.type === "Mobile")?.number ??
    phones[0]?.number ??
    ""
  );
}
